import { DetailsPanel, SpeedChip } from '@/components/controls';
import { useLocationOptions, useOptionLookup } from '@/hooks';
import { startCase } from '@/utils';
import { useDallasKeys } from '@/utils/config';
import { Box, Stack, Typography } from '@mui/material';
import { blue } from '@mui/material/colors';
import { format } from 'date-fns';
import { Popup } from 'react-map-gl/maplibre';
import { VehicleEquipmentIcons } from '../VehicleEquipmentIcons';
import { Status } from './Status';

function DriverDetailsPanel({ driver, isLastDriver = false }) {
  if (!driver?.code) return null;

  return (
    <DetailsPanel
      title={isLastDriver ? 'Last Driver' : 'Driver'}
      entries={[
        {
          label: 'Name',
          value: [driver.forenames, driver.surname]
            .filter(Boolean)
            .join(' ')
            .trim(),
        },
        { label: 'Staff #', value: driver.code },
        { label: 'Role', value: driver.role },
      ]}
      rightHeader={
        driver.collarNumber && (
          <Box
            sx={{
              fontSize: 10,
              color: 'white',
              bgcolor: blue[900],
              borderRadius: 1,
              px: 0.5,
              my: 0.25,
            }}
          >
            {driver.collarNumber}
          </Box>
        )
      }
    />
  );
}

export function VehiclePopup({
  hoverInfo: {
    longitude,
    latitude,
    feature: { properties },
  },
}) {
  const vehicleGroups = useOptionLookup('vehicleGroup');
  const vehicleRoles = useOptionLookup('vehicleRole');
  const homeStations = useLocationOptions();

  return (
    <Popup
      longitude={longitude}
      latitude={latitude}
      offset={16}
      closeButton={false}
    >
      <Stack spacing={0.5}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          {properties.vehicle?.registrationNumber ?? properties.imei}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {format(new Date(properties.time), 'dd/MM HH:mm:ss')}
        </Typography>
        <Status status={properties.status} />
        {properties.vehicle && (
          <DetailsPanel
            entries={[
              { label: 'Fleet #', value: properties.vehicle.fleetNumber },
              {
                label: 'Role',
                value:
                  vehicleRoles[properties.vehicle.role] ??
                  properties.vehicle.role,
              },
              {
                label: 'Home Station',
                value:
                  homeStations[properties.vehicle.homeStation]?.name ??
                  properties.vehicle.homeStation,
              },
              ...Object.entries(properties.vehicle.groups ?? {}).map(
                ([type, codes = []]) => ({
                  label: vehicleGroups[type]?.label ?? startCase(type),
                  value: codes.join?.(', ') ?? codes,
                }),
              ),
            ]}
          />
        )}
        <DriverDetailsPanel
          driver={properties.driver ?? properties.lastDriver}
          isLastDriver={!properties.driver}
        />
        {!properties.driver?.code &&
          !properties.lastDriver?.code &&
          properties.rfidCard && (
            <DetailsPanel
              title={useDallasKeys ? 'Dallas Key' : 'RFID Card'}
              entries={[
                {
                  label: 'Reference',
                  value: properties.rfidCard.reference,
                },
                {
                  label: 'Label',
                  value: properties.rfidCard.label,
                },
                {
                  label: 'Type',
                  value: properties.rfidCard.type,
                },
              ]}
            />
          )}
        <Stack
          spacing={1}
          direction="row"
          sx={{ justifyContent: 'space-between', pt: 0.5 }}
        >
          <SpeedChip
            ignitionOn={properties.ignitionOn}
            speedKilometresPerHour={properties.speedKilometresPerHour}
            speedLimitKilometresPerHour={
              properties.reverseGeocode?.speedLimitKilometresPerHour
            }
          />
          <VehicleEquipmentIcons properties={properties} />
        </Stack>
      </Stack>
    </Popup>
  );
}
